// §6.4 — canonical chords, and host translation (D-031).
//
// Canonical order is **Meta, Ctrl, Alt, Shift, key** — the order §6.4's own table is written
// in, so a keymap author writes what they read.
//
// `Meta` is the physical Command key on Apple hardware and the Windows key everywhere else.
// Browsers already report both as `metaKey`, so the translation is not about the event flag —
// it is about what the user is told to press. A macOS skin that tells a PC user to press ⌘ is
// the bug D-031 exists to prevent.

export interface Chord {
  meta: boolean;
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
  /** Canonical key name: `V`, `Space`, `ArrowLeft`, `F11`, `,`. */
  key: string;
}

export type HostPlatform = 'apple' | 'other';

const MODIFIER_ALIASES: Record<string, keyof Omit<Chord, 'key'>> = {
  meta: 'meta',
  cmd: 'meta',
  command: 'meta',
  super: 'meta',
  win: 'meta',
  ctrl: 'ctrl',
  control: 'ctrl',
  alt: 'alt',
  option: 'alt',
  opt: 'alt',
  shift: 'shift',
};

/** The `KeyboardEvent.key` each modifier reports, and §6.4's spelling of it. */
const MODIFIER_KEYS: Record<keyof Omit<Chord, 'key'>, { eventKey: string; canonical: string }> = {
  meta: { eventKey: 'Meta', canonical: 'Meta' },
  ctrl: { eventKey: 'Control', canonical: 'Ctrl' },
  alt: { eventKey: 'Alt', canonical: 'Alt' },
  shift: { eventKey: 'Shift', canonical: 'Shift' },
};

export interface ModifierTap {
  /** What `KeyboardEvent.key` reports for this modifier. */
  eventKey: string;
  /** §6.4's spelling, used as the binding's canonical form. */
  canonical: string;
}

/**
 * §6.4 — a binding written as a bare modifier (`Meta`, the Windows launcher) is a **tap**, not a
 * chord: it fires when the key comes back *up* having been pressed alone. `parseChord` rejects it,
 * which is correct — there is no key to combine with — so it is recognised separately (D-103).
 */
export function parseModifierTap(input: string): ModifierTap | null {
  const modifier = MODIFIER_ALIASES[input.trim().toLowerCase()];
  return modifier ? MODIFIER_KEYS[modifier] : null;
}

const NAMED_KEYS = new Set([
  'Space',
  'Tab',
  'Enter',
  'Escape',
  'Backspace',
  'Delete',
  'Home',
  'End',
  'PageUp',
  'PageDown',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
]);

function canonicalKey(raw: string): string | null {
  if (raw.length === 0) return null;
  if (/^[fF](1[0-2]|[1-9])$/.test(raw)) return `F${raw.slice(1)}`;

  const named = [...NAMED_KEYS].find((k) => k.toLowerCase() === raw.toLowerCase());
  if (named) return named;

  if (raw.length === 1) return /[a-z]/i.test(raw) ? raw.toUpperCase() : raw;
  return null;
}

export function parseChord(input: string): Chord | null {
  const parts = input
    .split('+')
    .map((p) => p.trim())
    .filter((p) => p.length > 0);

  // `Meta++` — a literal `+` as the key — survives the filter above only as its own segment.
  if (parts.length === 0) return input.includes('+') ? null : null;

  const chord: Chord = { meta: false, ctrl: false, alt: false, shift: false, key: '' };
  const keyParts: string[] = [];

  for (const part of parts) {
    const modifier = MODIFIER_ALIASES[part.toLowerCase()];
    if (modifier) chord[modifier] = true;
    else keyParts.push(part);
  }

  if (keyParts.length !== 1) return null;
  const key = canonicalKey(keyParts[0] as string);
  if (key === null) return null;

  return { ...chord, key };
}

export function formatChord(chord: Chord): string {
  const parts: string[] = [];
  if (chord.meta) parts.push('Meta');
  if (chord.ctrl) parts.push('Ctrl');
  if (chord.alt) parts.push('Alt');
  if (chord.shift) parts.push('Shift');
  parts.push(chord.key);
  return parts.join('+');
}

/** Normalise any accepted spelling to the canonical one. Returns `null` for a malformed chord. */
export function canonicalChord(input: string): string | null {
  const chord = parseChord(input);
  return chord === null ? null : formatChord(chord);
}

export interface KeyEventLike {
  key: string;
  code: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}

const CODE_KEYS: Array<[RegExp, (m: RegExpExecArray) => string]> = [
  [/^Key([A-Z])$/, (m) => m[1] as string],
  [/^Digit(\d)$/, (m) => m[1] as string],
  [/^(F\d{1,2})$/, (m) => m[1] as string],
  [/^(Arrow(?:Up|Down|Left|Right))$/, (m) => m[1] as string],
  [/^Space$/, () => 'Space'],
  [/^Tab$/, () => 'Tab'],
  [/^Enter$/, () => 'Enter'],
  [/^Escape$/, () => 'Escape'],
  [/^Backspace$/, () => 'Backspace'],
  [/^Comma$/, () => ','],
  [/^Period$/, () => '.'],
  [/^Slash$/, () => '/'],
  [/^Minus$/, () => '-'],
  [/^Equal$/, () => '='],
  [/^Semicolon$/, () => ';'],
  [/^Quote$/, () => "'"],
  [/^Backquote$/, () => '`'],
  [/^Bracket(Left|Right)$/, (m) => (m[1] === 'Left' ? '[' : ']')],
  [/^Backslash$/, () => '\\'],
];

/**
 * The canonical chord for a key event. `code` is preferred over `key` so a chord means the same
 * physical key whatever the layout, and so `Shift+1` is not read as `!`.
 */
/**
 * The `KeyboardEvent.key` names of a chord's modifiers — `Alt+Tab` → `['Alt']`. §6.4's
 * hold-and-cycle switcher (M6.5) commits when the *held* modifier comes back up, and which one
 * that is depends on the active skin's keymap, not on a hard-coded `Alt`.
 */
export function modifiersOf(input: string): string[] {
  const chord = parseChord(input);
  if (!chord) return [];
  const keys: string[] = [];
  if (chord.meta) keys.push('Meta');
  if (chord.ctrl) keys.push('Control');
  if (chord.alt) keys.push('Alt');
  if (chord.shift) keys.push('Shift');
  return keys;
}

export function chordFromEvent(event: KeyEventLike): string | null {
  let key: string | null = null;
  for (const [pattern, extract] of CODE_KEYS) {
    const match = pattern.exec(event.code);
    if (match) {
      key = extract(match);
      break;
    }
  }
  key ??= canonicalKey(event.key === ' ' ? 'Space' : event.key);
  if (key === null) return null;

  return formatChord({
    meta: event.metaKey,
    ctrl: event.ctrlKey,
    alt: event.altKey,
    shift: event.shiftKey,
    key,
  });
}

export function detectPlatform(userAgent: string, platform?: string): HostPlatform {
  const haystack = `${platform ?? ''} ${userAgent}`;
  return /mac|iphone|ipad|ipod/i.test(haystack) ? 'apple' : 'other';
}

const APPLE_LABELS = { meta: '⌘', ctrl: '⌃', alt: '⌥', shift: '⇧' } as const;
const OTHER_LABELS = { meta: 'Win', ctrl: 'Ctrl', alt: 'Alt', shift: 'Shift' } as const;

const KEY_LABELS: Record<string, string> = {
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
  Space: 'Space',
};

/**
 * D-031 — what the user is told to press on *their* hardware. Apple keyboards get the glyph
 * run (`⌘⇧V`); everything else gets a `+`-joined run naming the Windows key.
 */
export function chordLabel(input: string, platform: HostPlatform): string {
  const chord = parseChord(input);
  if (!chord) return input;

  const key = KEY_LABELS[chord.key] ?? chord.key;

  if (platform === 'apple') {
    let out = '';
    if (chord.ctrl) out += APPLE_LABELS.ctrl;
    if (chord.alt) out += APPLE_LABELS.alt;
    if (chord.shift) out += APPLE_LABELS.shift;
    if (chord.meta) out += APPLE_LABELS.meta;
    return out + key;
  }

  const parts: string[] = [];
  if (chord.meta) parts.push(OTHER_LABELS.meta);
  if (chord.ctrl) parts.push(OTHER_LABELS.ctrl);
  if (chord.alt) parts.push(OTHER_LABELS.alt);
  if (chord.shift) parts.push(OTHER_LABELS.shift);
  parts.push(key);
  return parts.join('+');
}

/** Chords the browser takes before Hangar can see them, outside immersive mode (§6.4). */
const BROWSER_RESERVED_KEYS = new Set(['W', 'T', 'N', 'Q']);

export function isBrowserReserved(input: string): boolean {
  const chord = parseChord(input);
  if (!chord) return false;
  return (chord.ctrl || chord.meta) && !chord.alt && BROWSER_RESERVED_KEYS.has(chord.key);
}
